import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Calendar, MapPin } from 'lucide-react';
import { Button } from './ui/button';

const EventCarousel = ({ events }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);

  // Get last 5 events
  const carouselEvents = events.slice(0, 5);

  const goToNext = useCallback(() => {
    if (isTransitioning || carouselEvents.length === 0) return;
    setIsTransitioning(true);
    setCurrentIndex((prev) => (prev + 1) % carouselEvents.length);
    setTimeout(() => setIsTransitioning(false), 700);
  }, [isTransitioning, carouselEvents.length]);

  const goToPrev = useCallback(() => {
    if (isTransitioning || carouselEvents.length === 0) return;
    setIsTransitioning(true);
    setCurrentIndex((prev) => (prev - 1 + carouselEvents.length) % carouselEvents.length);
    setTimeout(() => setIsTransitioning(false), 700);
  }, [isTransitioning, carouselEvents.length]);

  const goToSlide = (index) => {
    if (isTransitioning || index === currentIndex) return;
    setIsTransitioning(true);
    setCurrentIndex(index);
    setTimeout(() => setIsTransitioning(false), 700);
  };

  // Auto-advance carousel
  useEffect(() => {
    if (carouselEvents.length <= 1) return;
    
    const interval = setInterval(goToNext, 5000);
    return () => clearInterval(interval);
  }, [goToNext, carouselEvents.length]);

  if (carouselEvents.length === 0) return null;

  const currentEvent = carouselEvents[currentIndex];

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('es-HN', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const getMinPrice = (ticketTypes) => {
    if (!ticketTypes || ticketTypes.length === 0) return 0;
    return Math.min(...ticketTypes.map(t => t.price));
  };

  return (
    <section className="relative w-full h-[500px] md:h-[600px] overflow-hidden bg-background" data-testid="event-carousel">
      {/* Background Images with Transition */}
      <div className="absolute inset-0">
        {carouselEvents.map((event, index) => (
          <div
            key={event.event_id}
            className={`absolute inset-0 transition-all duration-700 ease-in-out ${
              index === currentIndex 
                ? 'opacity-100 scale-100' 
                : 'opacity-0 scale-105'
            }`}
          >
            {/* Event Image */}
            <div
              className="absolute inset-0 bg-cover bg-center"
              style={{
                backgroundImage: `url(${event.image_url || 'https://images.unsplash.com/photo-1492684223066-81342ee5ff30?w=1600'})`,
              }}
            />
            {/* Gradient Overlay */}
            <div className="absolute inset-0 bg-gradient-to-t from-background via-background/70 to-transparent" />
            <div className="absolute inset-0 bg-gradient-to-r from-background/90 via-background/50 to-transparent" />
          </div>
        ))}
      </div>

      {/* Content */}
      <div className="relative z-10 h-full max-w-7xl mx-auto px-4 flex items-center">
        <div className="max-w-2xl">
          {/* Badge */}
          <div 
            className={`inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/20 border border-primary/30 mb-4 transition-all duration-500 ${
              isTransitioning ? 'opacity-0 translate-y-4' : 'opacity-100 translate-y-0'
            }`}
          >
            <span className="w-2 h-2 bg-primary rounded-full animate-pulse" />
            <span className="text-sm text-primary font-medium">Evento Destacado</span>
          </div>

          {/* Title */}
          <h2 
            className={`text-3xl md:text-5xl font-bold text-white mb-4 transition-all duration-500 delay-75 ${
              isTransitioning ? 'opacity-0 translate-y-4' : 'opacity-100 translate-y-0'
            }`}
          >
            {currentEvent?.title}
          </h2>

          {/* Event Details */}
          <div 
            className={`flex flex-wrap gap-4 mb-6 text-white/80 transition-all duration-500 delay-100 ${
              isTransitioning ? 'opacity-0 translate-y-4' : 'opacity-100 translate-y-0'
            }`}
          >
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-primary" />
              <span className="text-sm">{formatDate(currentEvent?.date)}</span>
            </div>
            <div className="flex items-center gap-2">
              <MapPin className="w-4 h-4 text-primary" />
              <span className="text-sm">{currentEvent?.location}</span>
            </div>
          </div>

          {/* Description */}
          <p 
            className={`text-white/70 mb-6 line-clamp-2 max-w-xl transition-all duration-500 delay-150 ${
              isTransitioning ? 'opacity-0 translate-y-4' : 'opacity-100 translate-y-0'
            }`}
          >
            {currentEvent?.description}
          </p>

          {/* Price & CTA */}
          <div 
            className={`flex items-center gap-4 transition-all duration-500 delay-200 ${
              isTransitioning ? 'opacity-0 translate-y-4' : 'opacity-100 translate-y-0'
            }`}
          >
            <div className="text-white">
              <span className="text-sm text-white/60">Desde</span>
              <p className="text-2xl font-bold text-primary">
                {currentEvent?.is_free ? 'GRATIS' : `L. ${getMinPrice(currentEvent?.ticket_types).toLocaleString()}`}
              </p>
            </div>
            <Link to={`/events/${currentEvent?.event_id}`}>
              <Button className="btn-primary px-8 py-6 text-base" data-testid="carousel-cta-button">
                Comprar Tickets
              </Button>
            </Link>
          </div>
        </div>
      </div>

      {/* Navigation Arrows */}
      {carouselEvents.length > 1 && (
        <>
          <button
            onClick={goToPrev}
            className="absolute left-4 top-1/2 -translate-y-1/2 z-20 w-12 h-12 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 flex items-center justify-center text-white hover:bg-white/20 transition-all duration-300 hover:scale-110"
            aria-label="Anterior"
            data-testid="carousel-prev"
          >
            <ChevronLeft className="w-6 h-6" />
          </button>
          <button
            onClick={goToNext}
            className="absolute right-4 top-1/2 -translate-y-1/2 z-20 w-12 h-12 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 flex items-center justify-center text-white hover:bg-white/20 transition-all duration-300 hover:scale-110"
            aria-label="Siguiente"
            data-testid="carousel-next"
          >
            <ChevronRight className="w-6 h-6" />
          </button>
        </>
      )}

      {/* Dots Indicator */}
      {carouselEvents.length > 1 && (
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20 flex items-center gap-2">
          {carouselEvents.map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              className={`transition-all duration-300 rounded-full ${
                index === currentIndex 
                  ? 'w-8 h-2 bg-primary' 
                  : 'w-2 h-2 bg-white/40 hover:bg-white/60'
              }`}
              aria-label={`Ir al slide ${index + 1}`}
              data-testid={`carousel-dot-${index}`}
            />
          ))}
        </div>
      )}

      {/* Progress Bar */}
      {carouselEvents.length > 1 && (
        <div className="absolute bottom-0 left-0 right-0 h-1 bg-white/10 z-20">
          <div 
            className="h-full bg-primary transition-all duration-300"
            style={{ width: `${((currentIndex + 1) / carouselEvents.length) * 100}%` }}
          />
        </div>
      )}
    </section>
  );
};

export default EventCarousel;
